import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListPayrollWorkers200 } from "@/api/financial";
import {
	listPayrollWorkersQueryOptions,
	useCreatePayrollWorker,
	useUpdatePayrollWorker,
} from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import { SubmitButton } from "@/components/ui/submit-button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { formatMoney } from "@/utils/financial";
import {
	FinancialHeaderActions,
	HeaderActionButton,
} from "../../-components/FinancialHeaderActions";
import { EmploymentTypeBadge } from "../-components/PayrollBadges";
import { PayrollGate } from "../-components/PayrollGate";
import { WorkerForm, type WorkerFormValues } from "../-components/WorkerForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/payroll/workers/",
)({
	component: PayrollWorkersPage,
});

type Worker = ListPayrollWorkers200["data"][number];

function PayrollWorkersPage() {
	const { orgId } = Route.useParams();
	return (
		<PayrollGate orgId={orgId}>
			<WorkersContent orgId={orgId} />
		</PayrollGate>
	);
}

function WorkersContent({ orgId }: { orgId: string }) {
	const { t } = useTranslation();
	const workerFormId = useId();
	const [editing, setEditing] = useState<Worker | null>(null);
	const [isCreating, setIsCreating] = useState(false);

	const { data, isLoading } = useQuery(
		listPayrollWorkersQueryOptions(orgId, { limit: "200" }),
	);
	const rows = data?.data ?? [];

	const createMutation = useCreatePayrollWorker();
	const updateMutation = useUpdatePayrollWorker();
	const isSaving = createMutation.isPending || updateMutation.isPending;

	const closeDialog = () => {
		setEditing(null);
		setIsCreating(false);
	};

	const editDefaults: Partial<WorkerFormValues> | undefined = editing
		? {
				name: editing.name,
				cpf: editing.cpf ?? undefined,
				role: editing.role ?? undefined,
				employmentType: editing.employmentType,
				baseSalary: editing.baseSalary ?? undefined,
				dayRate: editing.dayRate ?? undefined,
				defaultEstablishmentId: editing.defaultEstablishmentId ?? undefined,
				isActive: editing.isActive,
			}
		: undefined;

	return (
		<div className="flex flex-col gap-4">
			<FinancialHeaderActions>
				<HeaderActionButton
					icon={Plus}
					label={t("financial.payroll.workers.new")}
					shortLabel={t("financial.payroll.workers.newShort")}
					onClick={() => setIsCreating(true)}
				/>
			</FinancialHeaderActions>

			<PageHeader title={t("financial.payroll.workers.title")} showBackButton />

			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					{isLoading ? (
						<div className="flex justify-center py-16">
							<LoadingSpinner />
						</div>
					) : rows.length === 0 ? (
						<div className="py-12 text-center">
							<p className="font-medium">
								{t("financial.payroll.workers.empty.title")}
							</p>
							<p className="mt-1 text-sm text-base-content/60">
								{t("financial.payroll.workers.empty.description")}
							</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("financial.payroll.workers.columns.name")}
									</TableHead>
									<TableHead className="hidden sm:table-cell">
										{t("financial.payroll.workers.columns.role")}
									</TableHead>
									<TableHead>
										{t("financial.payroll.workers.columns.employmentType")}
									</TableHead>
									<TableHead className="text-right">
										{t("financial.payroll.workers.columns.rate")}
									</TableHead>
									<TableHead>
										{t("financial.payroll.workers.columns.status")}
									</TableHead>
									<TableHead className="text-right" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{rows.map((worker) => (
									<TableRow
										key={worker.id}
										className={worker.isActive ? "" : "opacity-60"}
									>
										<TableCell>
											<span className="font-medium">{worker.name}</span>
											{worker.cpf && (
												<span className="block text-xs text-base-content/60">
													{worker.cpf}
												</span>
											)}
										</TableCell>
										<TableCell className="hidden sm:table-cell text-base-content/70">
											{worker.role ?? "—"}
										</TableCell>
										<TableCell>
											<EmploymentTypeBadge type={worker.employmentType} />
										</TableCell>
										<TableCell className="text-right">
											{worker.employmentType === "MONTHLY" && worker.baseSalary
												? formatMoney(worker.baseSalary)
												: worker.employmentType === "DAILY" && worker.dayRate
													? `${formatMoney(worker.dayRate)} / ${t("financial.payroll.workers.perDay")}`
													: "—"}
										</TableCell>
										<TableCell>
											{worker.isActive ? (
												<span className="badge badge-success badge-outline">
													{t("financial.payroll.workers.active")}
												</span>
											) : (
												<span className="badge badge-ghost">
													{t("financial.payroll.workers.inactive")}
												</span>
											)}
										</TableCell>
										<TableCell className="text-right">
											<Button
												size="sm"
												variant="ghost"
												onClick={() => setEditing(worker)}
											>
												<Pencil className="mr-1 h-3 w-3" />
												{t("common.edit")}
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			</div>

			<ResponsiveFormDialog
				open={isCreating || editing !== null}
				onOpenChange={(open) => !open && closeDialog()}
				title={
					editing
						? t("financial.payroll.workers.editTitle")
						: t("financial.payroll.workers.newTitle")
				}
				pending={isSaving}
				footer={
					<>
						<Button
							type="button"
							variant="outline"
							disabled={isSaving}
							onClick={closeDialog}
						>
							{t("common.cancel")}
						</Button>
						<SubmitButton form={workerFormId} pending={isSaving}>
							{t("common.save")}
						</SubmitButton>
					</>
				}
			>
				<WorkerForm
					defaultValues={editDefaults}
					formId={workerFormId}
					onSubmit={(payload) => {
						if (editing) {
							return updateMutation
								.mutateAsync({
									organizationId: orgId,
									workerId: editing.id,
									data: payload,
								})
								.then(closeDialog);
						}
						return createMutation
							.mutateAsync({ organizationId: orgId, data: payload })
							.then(closeDialog);
					}}
				/>
			</ResponsiveFormDialog>
		</div>
	);
}
